import { gsap, Power2 } from 'gsap';
import { Transition } from '../Transition';
import { shapes } from './shapes';
import '../types';

export class Ripple extends Transition {
  /**
   * @constructor
   * @class Ripple
   * @classdesc Controls all Ripple transitions
   * @param {options} options
   */
  constructor(options) {
    super(options);

    const defaultOptions = {
      svg: shapes.circle,
    };

    this.options = { ...defaultOptions, ...options, ...this.options };

    this.bindGlobalListeners();
    this.bindListeners();
  }

  /**
   * Binds global listeners
   */
  bindGlobalListeners() {
    document.addEventListener('_page_loaded', this.bindListeners.bind(this));
  }

  /**
   * Binds DOM listeners
   */
  bindListeners() {
    document
      .querySelectorAll('a:not([target="_blank"])')
      ?.forEach((link) => link.addEventListener('click', this._setCursorPosition.bind(this), true));
  }

  /**
   * Sets coordinates of the clicked link
   * @param {MouseEvent} e
   */
  _setCursorPosition(e) {
    this.cursor = {
      x: e.clientX,
      y: e.clientY,
    };
  }

  /**
   * Creates the SVG element and sets its properties
   * @returns {HTMLElement} SVG element
   */
  _createShape() {
    const { svg } = this.options;
    const { x, y } = this.cursor;
    const shape = new DOMParser().parseFromString(svg, 'image/svg+xml').documentElement;

    this._diameter = Math.max(window.innerWidth, window.innerHeight) * 2;
    const radius = this._diameter / 2;

    gsap.set(shape, {
      attr: {
        class: 'wb-transition-ripple',
      },
      left: `${x - radius}px`,
      top: `${y - radius}px`,
      opacity: 0,
    });

    return shape;
  }

  /**
   * Method for the animation "in"
   * @returns {Promise}
   */
  in() {
    const duration = this._getOptionValue('duration');
    const container = this.createTransitionContainer();
    const shape = this._createShape();
    const tl = new gsap.timeline();

    container.appendChild(shape);

    tl.set(shape, {
      attr: {
        class: 'wb-transition-ripple wb-transition-ripple--in',
      },
      width: this._diameter,
      height: this._diameter,
      opacity: 1,
      animationDuration: duration,
    });

    return new Promise((resolve) =>
      setTimeout(() => {
        document.dispatchEvent(new Event('barbaAnimInEnd'));
        resolve();
      }, duration * 1000)
    );
  }

  /**
   * Method for the animation "out"
   * @returns {Promise}
   */
  out() {
    const duration = this._getOptionValue('duration');
    const shape = document.querySelector('.wb-transition-ripple');
    const tl = new gsap.timeline();

    tl.set(shape, {
      attr: {
        class: 'wb-transition-ripple wb-transition-ripple--out',
      },
      animationDuration: duration,
    });

    document.dispatchEvent(new Event('barbaAnimOutStart'));

    return new Promise((resolve) =>
      setTimeout(() => {
        this.getTransitionContainer().remove();
        resolve();
      }, duration * 1000)
    );
  }
}
